import React, { useState, useEffect } from 'react';
import * as userService from '../../../../../services/userService';
import { formatUserName } from '../../../../../services/userProfileService';
import ResponsiveModal from '../../../../common/ResponsiveModal';
import { ResponsiveButton, ResponsiveGrid } from '../../../../common/ResponsiveLayout';

const EditProfileModal = ({ isOpen, user, currentUser, userRole, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    email: '',
    role: 'CLIENT',
    coach_id: '',
    avatar_url: ''
  });
  const [coaches, setCoaches] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if current user can edit specific fields
  const canEditField = (fieldName) => {
    // Super admin can edit everything
    if (userRole === 'SUPER_ADMIN') return true;
    
    // Users can edit their own basic profile info (not role or coach)
    if (currentUser?.id === user?.id) {
      return ['first_name', 'last_name', 'full_name', 'avatar_url'].includes(fieldName);
    }
    
    // Coaches can edit their clients' basic info (not role)
    if (userRole === 'COACH' && user?.coach_id === currentUser?.id) {
      return ['first_name', 'last_name', 'full_name', 'avatar_url'].includes(fieldName);
    }
    
    return false;
  };

  // Overall edit permission check
  const canEdit = () => {
    return userRole === 'SUPER_ADMIN' || currentUser?.id === user?.id;
  };

  // Fetch coaches when component mounts
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!canEditField('coach_id')) return;
      
      try {
        const { data, error } = await userService.getCoaches();
        if (error) {
          console.error('Error fetching coaches:', error);
        } else {
          setCoaches(data || []);
        }
      } catch (err) {
        console.error('Error fetching coaches:', err);
      }
    };

    if (isOpen) {
      fetchCoaches();
    }
  }, [isOpen]);

  // Initialize form data when modal opens or user changes
  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        full_name: user.full_name || '',
        email: user.email || '',
        role: user.role || 'CLIENT',
        coach_id: user.coach_id || '',
        avatar_url: user.avatar_url || ''
      });
      setErrors({});
    }
  }, [user, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Avatar URL validation (if provided)
    if (formData.avatar_url && formData.avatar_url.trim()) {
      try {
        new URL(formData.avatar_url);
      } catch {
        newErrors.avatar_url = 'Please enter a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: value,
      };

      // Auto-generate full_name when first_name or last_name changes
      if (name === 'first_name' || name === 'last_name') {
        const firstName = name === 'first_name' ? value : prev.first_name;
        const lastName = name === 'last_name' ? value : prev.last_name;
        newFormData.full_name = `${firstName} ${lastName}`.trim();
      }

      return newFormData;
    });

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Only send fields that are allowed to be edited
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (canEditField(key) && formData[key] !== user[key]) {
          updateData[key] = formData[key];
        }
      });

      // Always include email for super admins
      if (userRole === 'SUPER_ADMIN' && formData.email !== user.email) {
        updateData.email = formData.email;
      }

      if (Object.keys(updateData).length === 0) {
        setErrors({ general: 'No changes detected' });
        return;
      }

      await userService.updateUser(user.id, updateData);
      
      // Call parent's onSave callback with updated data
      if (onSave) {
        onSave({ ...user, ...updateData });
      }
      
      onCancel(); // Close modal on success
    } catch (error) {
      console.error("Failed to update profile:", error);
      setErrors({ general: error.message || "Failed to update profile. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onCancel}
      title={`Edit Profile - ${formatUserName(user)}`}
      size="lg"
      closeOnBackdrop={!isSubmitting}
    >
      <div className="p-6">
        {/* Permission check */}
        {!canEdit() && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            You don't have permission to edit this profile.
          </div>
        )}

        {/* Error display */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {errors.general}
          </div>
        )}

        {canEdit() && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First and Last Name */}
            <ResponsiveGrid columns={{ xs: 1, sm: 2 }} gap={4}>
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  id="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  disabled={!canEditField('first_name') || isSubmitting}
                  className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors ${
                    errors.first_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  } ${!canEditField('first_name') ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  required
                />
                {errors.first_name && (
                  <p className="mt-2 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  id="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  disabled={!canEditField('last_name') || isSubmitting}
                  className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors ${
                    errors.last_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  } ${!canEditField('last_name') ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  required
                />
                {errors.last_name && (
                  <p className="mt-2 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </ResponsiveGrid>

            {/* Full Name */}
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                id="full_name"
                value={formData.full_name}
                onChange={handleChange}
                disabled={!canEditField('full_name') || isSubmitting}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm bg-gray-50 cursor-not-allowed"
                placeholder="Auto-generated from first and last name"
                readOnly
              />
              <p className="mt-1 text-xs text-gray-500">Auto-generated from first and last name</p>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                disabled={userRole !== 'SUPER_ADMIN' || isSubmitting}
                className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors ${
                  errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                } ${userRole !== 'SUPER_ADMIN' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                required
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
              {userRole !== 'SUPER_ADMIN' && (
                <p className="mt-1 text-xs text-gray-500">Only super admins can edit email addresses</p>
              )}
            </div>

            {/* Role and Coach on larger screens, stacked on mobile */}
            <ResponsiveGrid columns={{ xs: 1, md: 2 }} gap={4}>
              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  disabled={!canEditField('role') || isSubmitting}
                  className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors ${
                    !canEditField('role') ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="CLIENT">CLIENT</option>
                  <option value="COACH">COACH</option>
                  <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                </select>
                {!canEditField('role') && (
                  <p className="mt-1 text-xs text-gray-500">Only super admins can edit user roles</p>
                )}
              </div>

              {/* Coach Assignment */}
              <div>
                <label htmlFor="coach_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Coach
                </label>
                <select
                  id="coach_id"
                  name="coach_id"
                  value={formData.coach_id}
                  onChange={handleChange}
                  disabled={!canEditField('coach_id') || isSubmitting}
                  className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors ${
                    !canEditField('coach_id') ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">No coach assigned</option>
                  {coaches.map((coach) => (
                    <option key={coach.id} value={coach.id}>
                      {coach.first_name && coach.last_name
                        ? `${coach.first_name} ${coach.last_name}`
                        : coach.full_name || coach.email}
                    </option>
                  ))}
                </select>
                {!canEditField('coach_id') && (
                  <p className="mt-1 text-xs text-gray-500">Only super admins can change coach assignments</p>
                )}
              </div>
            </ResponsiveGrid>

            {/* Avatar URL */}
            <div>
              <label htmlFor="avatar_url" className="block text-sm font-medium text-gray-700 mb-2">
                Avatar URL
              </label>
              <input
                type="url"
                name="avatar_url"
                id="avatar_url"
                value={formData.avatar_url}
                onChange={handleChange}
                disabled={!canEditField('avatar_url') || isSubmitting}
                className={`w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 transition-colors ${
                  errors.avatar_url ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                } ${!canEditField('avatar_url') ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                placeholder="https://example.com/avatar.jpg"
              />
              {errors.avatar_url && (
                <p className="mt-2 text-sm text-red-600">{errors.avatar_url}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">URL to profile picture</p>
            </div>

            {/* Form actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-4 border-t border-gray-200">
              <ResponsiveButton
                type="button"
                onClick={onCancel}
                variant="secondary"
                disabled={isSubmitting}
                size={{ xs: 'lg', sm: 'md' }}
                fullWidth={false}
              >
                Cancel
              </ResponsiveButton>
              <ResponsiveButton
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                size={{ xs: 'lg', sm: 'md' }}
                fullWidth={false}
              >
                {isSubmitting ? 'Updating...' : 'Update Profile'}
              </ResponsiveButton>
            </div>
          </form>
        )}
      </div>
    </ResponsiveModal>
  );
};

export default EditProfileModal;